
import React, { useMemo, useState } from "react";
import CardsPoints from "../components/CardsPoints";
import SeeMore from "../components/SeeMore";
import ReflexSelect from "../components/ReflexSelect";
import Cards from "../components/Cards";

import Vision from "../components/Vision";
import useReflex from "../hooks/useReflex";
import { obterMesesMaisRecentes } from "../utils";
import { coordenadorMock } from "../utils/coordenadorMock";
import useModalAction from "~components/Modal/hooks/useModalAction";


const ReflexCurrentQuarter = () => {
  const [selectedVendedorId, setSelectedVendedorId] = useState(null);
  const [open, setOpen] = useState(false);
  const { reflex, loading } = useReflex()
  const vendedores = useMemo(() => reflex, []);


  const selectOptions = useMemo(() => {
    if (vendedores.length > 1) {
      return [
        { label: 'Visão Geral', value: 0 },
        ...vendedores.map((vendedor) => ({
          label: vendedor.nomeVendedor,
          value: vendedor.id,
        })),
      ];
    }

    return false;
  }, [vendedores]);

const vendedorSelecionado = useMemo(() => {
  if (!vendedores?.length) return null;

  if (vendedores.length === 1) return vendedores[0];

  if (selectedVendedorId === 0) return vendedores;

  return vendedores.find(v => v.id === selectedVendedorId) || null;
}, [selectedVendedorId, vendedores]);


  const points = useMemo(() => {
    if (!vendedorSelecionado?.meses?.length) return null;


    const mesesOrdenados = [...vendedorSelecionado.meses]
      .sort((a, b) => new Date(b.mes) - new Date(a.mes))
      .slice(0, 3);

    let pontuacaoAtualTotal = 0;
    let pontuacaoCampanhaTotal = 0;
    let percentualSemCreditoTotal = 0;
    let percentualFinalTotal = 0;
    let mesesComDados = 0;

    mesesOrdenados.forEach((mes) => {
      if (!mes?.reflex?.length) return;

      const allItems = mes.reflex;


      const pontuacaoAtual = allItems
        .filter((item) => item.tipo.toUpperCase() === "REFLEX" && item.atingimentos?.ponderadoSemCredito != null)
        .reduce((acc, item) => acc + Number(item.atingimentos.ponderadoSemCredito), 0);


      const pontuacaoCampanha = allItems
        .filter((item) => item.tipo.toUpperCase() === "CAMPANHA" && item.atingimentos?.projetado != null)
        .reduce((acc, item) => acc + Number(item.atingimentos.projetado), 0);


      const reflexItems = allItems.filter((item) => item.tipo.toUpperCase() === "REFLEX");
      const mediaSemCredito =
        reflexItems.reduce((acc, item) => acc + Number(item.percentualSalarioSemCreditoSemCampanha || 0), 0) /
        (reflexItems.length || 1);

      const mediaFinal =
        allItems.reduce((acc, item) => acc + Number(item.percentualSalarioSemCredito || 0), 0) / (allItems.length || 1);


      const pontuacaoFinal = Math.min(pontuacaoAtual + pontuacaoCampanha, 500);

      pontuacaoAtualTotal += pontuacaoAtual;
      pontuacaoCampanhaTotal += pontuacaoCampanha;
      percentualSemCreditoTotal += mediaSemCredito;
      percentualFinalTotal += mediaFinal;
      mesesComDados++;
    });

    if (mesesComDados === 0) return null;

    return {
      id: vendedorSelecionado.id,
      codigoVendedor: vendedorSelecionado.codigoVendedor,
      nomeVendedor: vendedorSelecionado.nomeVendedor,
      pontuacaoAtual: pontuacaoAtualTotal.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      pontuacaoCampanha: pontuacaoCampanhaTotal.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      pontuacaoFinal: (pontuacaoAtualTotal < 100
        ? 0
        : pontuacaoAtualTotal + pontuacaoCampanhaTotal
      ).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      percentualSalarioSemCreditoSemCampanha: percentualSemCreditoTotal / mesesComDados,
      percentualSalarioFinal: percentualFinalTotal / mesesComDados,
    };
  }, [vendedorSelecionado]);

  const { semCredito, comCredito } = useMemo(() => {
    const meses = vendedorSelecionado?.meses;
    if (!meses?.length) return { semCredito: [], comCredito: [] };


    const mesesTrimestre = [...meses]
      .sort((a, b) => new Date(b.mes) - new Date(a.mes))
      .slice(0, 3);

    const semCredito = [];
    const comCredito = [];


    mesesTrimestre.forEach((mes) => {
      for (const { kpi, pesoPercentual, atingimentos } of mes.reflex || []) {
        if (!atingimentos) continue;

        const { projetado, realComCredito, ponderadoSemCredito, final } = atingimentos;
        const isCampanha = kpi?.toLowerCase().includes("vloz - split");
        const itemNome = isCampanha ? "Campanha" : kpi;

        if (projetado != null) {
          const existingItem = semCredito.find(item => item.nome === itemNome);
          if (existingItem) {
            existingItem.pontos += projetado;
            existingItem.ponderados += ponderadoSemCredito;
          } else {
            semCredito.push({
              nome: itemNome,
              percentual: pesoPercentual,
              pontos: ponderadoSemCredito,
              ponderados: ponderadoSemCredito,
              isCampanha,
            });
          }
        }

        if (realComCredito != null) {
          const existingItem = comCredito.find(item => item.nome === itemNome);
          if (existingItem) {
            existingItem.pontos += realComCredito;
            existingItem.ponderados += ponderadoSemCredito;
          } else {
            comCredito.push({
              nome: itemNome,
              percentual: pesoPercentual,
              pontos: final,
              ponderados: final,
              isCampanha,
            });
          }
        }
      }
    });

    const itensValidosSemCredito = semCredito.filter((i) => !i.isCampanha);
    const itensValidosComCredito = comCredito.filter((i) => !i.isCampanha);

    const mediaSemCredito = itensValidosSemCredito.reduce((sum, item) => sum + item.ponderados, 0);
    const mediaComCredito = itensValidosComCredito.reduce((sum, item) => sum + item.ponderados, 0);

    const campanhaSemCredito = semCredito.find((i) => i.isCampanha);
    const campanhaComCredito = comCredito.find((i) => i.isCampanha);

    const pontuacaoFinalSemCredito = mediaSemCredito < 100 ? 0 : mediaSemCredito + (campanhaSemCredito?.pontos ?? 0);
    const pontuacaoFinalComCredito = mediaComCredito < 100 ? 0 : mediaComCredito + (campanhaComCredito?.pontos ?? 0);

    let percentualSemCreditoTotal = 0;
    let percentualComCreditoTotal = 0;
    let mesesComDados = 0;

    mesesTrimestre.forEach((mes) => {
      const todosItensValidos = mes.reflex?.filter((item) => item.tipo.toUpperCase() === "REFLEX");
      if (!todosItensValidos?.length) return;

      const percentualSemCredito = todosItensValidos.reduce((acc, item) =>
        acc + Number(item.percentualSalarioSemCreditoSemCampanha || 0), 0) / todosItensValidos.length;

      const percentualComCredito =
    todosItensValidos.reduce((acc, item) => acc + Number(item.percentualSalarioSemCredito || 0), 0) /
    (todosItensValidos.length || 1);

      percentualSemCreditoTotal += percentualSemCredito;
      percentualComCreditoTotal += percentualComCredito;
      mesesComDados++;
    });

    const percentualSemCredito = mesesComDados > 0 ? percentualSemCreditoTotal / mesesComDados : 0;
    const percentualComCredito = mesesComDados > 0 ? percentualComCreditoTotal / mesesComDados : 0;

    const formatar = (valor) =>
      valor != null ? valor.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "";

    const formatarLista = (lista, media, pontuacaoFinal, percentual) => {
      const campanhas = lista.filter((item) => item.isCampanha);
      const normais = lista.filter((item) => !item.isCampanha);

      return [
        ...normais.map((item) => ({
          ...item,
          pontos: formatar(item.pontos),
        })),
        {
          nome: "Soma de Pontos",
          percentual: null,
          pontos: formatar(media),
        },
        ...campanhas.map((item) => ({
          ...item,
          pontos: formatar(item.pontos),
        })),
        {
          nome: "Pontuação Final",
          percentual: null,
          pontos: formatar(pontuacaoFinal),
          sub: `${percentual.toLocaleString("pt-BR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })}% do salário`,
        },
      ];
    };

    const listaSemCredito = semCredito.length
      ? formatarLista(semCredito, mediaSemCredito, pontuacaoFinalSemCredito, percentualSemCredito)
      : [];

    const listaComCredito = comCredito.length
      ? formatarLista(comCredito, mediaComCredito, pontuacaoFinalComCredito, percentualComCredito)
      : [];

    return { semCredito: listaSemCredito, comCredito: listaComCredito };
  }, [vendedorSelecionado]);

  const dataCards = useMemo(() => {
    if (!vendedorSelecionado?.meses?.length) return [];

    const mesesTrimestre = [...vendedorSelecionado.meses]
      .sort((a, b) => new Date(b.mes) - new Date(a.mes))
      .slice(0, 3);


    const todosItens = mesesTrimestre.flatMap(mes => mes.reflex || []);

    return todosItens;
  }, [vendedorSelecionado]);

  const vision = useMemo(() => {
    if (!Array.isArray(vendedorSelecionado) || vendedorSelecionado.length === 0) return [];

    return vendedorSelecionado
      .map((vendedor) => {
        if (!vendedor?.meses?.length) return null;


        const mesesTrimestre = [...vendedor.meses]
          .sort((a, b) => new Date(b.mes) - new Date(a.mes))
          .slice(0, 3);

        let realTotal = 0;
        let projTotal = 0;

        mesesTrimestre.forEach((mes) => {
          const { real, proj } = (mes.reflex || []).reduce(
            (acc, item) => {

              if (item.tipo === "Campanha") return acc;

              acc.real += Number(item.atingimentos?.real || 0);
              acc.proj += Number(item.atingimentos?.projetado || 0);
              return acc;
            },
            { real: 0, proj: 0 }
          );

          realTotal += real;
          projTotal += proj;
        });

        return {
          nome: vendedor.nomeVendedor,
          real: realTotal,
          proj: projTotal,
        };
      })
      .filter(Boolean);
  }, [vendedorSelecionado, selectedVendedorId]);

  const mesRecente = obterMesesMaisRecentes(vendedorSelecionado, vendedores);


  return (
    <div className="">
      <div className="bg-white pt-4">
        <div className="bg-white border border-gray-200 rounded-lg shadow-md p-8 mt-8">
          <ReflexSelect selectOptions={selectOptions} mesRecente={mesRecente} setSelectedVendedorId={setSelectedVendedorId} selectedVendedorId={selectedVendedorId} trimestre={true} mesAtual={false} vendedores={vendedores} loading={loading} />
          <CardsPoints points={points} />
        </div>
      </div>
      <SeeMore semCredito={semCredito} comCredito={comCredito} open={open} setOpen={setOpen} />
      <Cards dataCards={dataCards} vendedores={vendedores} />
      {selectedVendedorId === 0 && vendedores.length > 1 && <Vision vision={vision} />}
    </div>
  );
};

export default ReflexCurrentQuarter;